import { useEffect, useRef, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  TextInput,
  NumberInput,
  Textarea,
  Button,
  Stack,
  Group,
  Loader,
  Alert,
  MultiSelect,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCreateAwardSheet, useUpdateAwardSheet, useMeeting, useAwardSheet } from '../hooks';
import { AgendaItem } from '../../../api/bidCommittee';
import { IconAlertCircle } from '@tabler/icons-react';
import { RegulatoryClausePicker } from '../../../components/RegulatoryClausePicker';

interface AwardSheetFormValues {
  meetingId: string;
  tenderId?: string;
  singleSourceId?: string;
  projectSummarySheetId?: string;
  tenderNumber?: string;
  tenderName: string;
  location: string;
  quotationNumber?: string;
  vendor: string;
  cost: number;
  duration: number;
  decisionText: string;
  justification: string;
  detail?: string;
  acknowledgement?: string;
}

export function AwardSheetForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = !!id;

  const { data: awardSheet, isLoading: awardSheetLoading } = useAwardSheet(id || '');
  const resolvedMeetingId =
    searchParams.get('meetingId') || awardSheet?.meetingId || '';
  const { data: meeting, isLoading: meetingLoading } = useMeeting(
    resolvedMeetingId || undefined
  );
  const createAwardSheet = useCreateAwardSheet();
  const updateAwardSheet = useUpdateAwardSheet();

  const [selectedAgendaItemIds, setSelectedAgendaItemIds] = useState<string[]>([]);
  const [agendaSelectionError, setAgendaSelectionError] = useState<string | null>(null);

  const form = useForm<AwardSheetFormValues>({
    initialValues: {
      meetingId: resolvedMeetingId,
      tenderId: '',
      singleSourceId: '',
      projectSummarySheetId: '',
      tenderNumber: '',
      tenderName: '',
      location: '',
      quotationNumber: '',
      vendor: '',
      cost: 0,
      duration: 0,
      decisionText: '',
      justification: '',
      detail: '',
      acknowledgement: '',
    },
    validate: {
      meetingId: (value) => (!value ? 'Meeting is required' : null),
      location: (value) => (!value ? 'Location is required' : null),
      vendor: (value) => (!value ? 'Vendor is required' : null),
      cost: (value) => (value <= 0 ? 'Cost must be greater than 0' : null),
      duration: (value) => (value <= 0 ? 'Duration must be greater than 0' : null),
      decisionText: (value) => (!value ? 'Decision text is required' : null),
      justification: (value) => (!value ? 'Justification is required' : null),
    },
  });

  const formRef = useRef(form);
  formRef.current = form;

  // Sync Thaana fields before form re-renders
  const syncThaanaFieldsBeforeRender = () => {
    const containers = document.querySelectorAll('.thaana-input');
    containers.forEach((container) => {
      const input = container.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement;
      if (input) {
        const fieldName = input.getAttribute('name');
        if (fieldName && formRef.current.values.hasOwnProperty(fieldName)) {
          const currentValue = input.value;
          const formValue = formRef.current.values[fieldName as keyof AwardSheetFormValues];
          if (currentValue !== formValue) {
            formRef.current.setFieldValue(fieldName as keyof AwardSheetFormValues, currentValue);
          }
        }
      }
    });
  };

  // Auto-resize textarea function
  const autoResizeTextarea = (textarea: HTMLTextAreaElement | null) => {
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  // Handle textarea input with auto-resize
  const handleTextareaChange = (fieldName: keyof AwardSheetFormValues, value: string) => {
    syncThaanaFieldsBeforeRender();
    form.setFieldValue(fieldName, value);
    
    // Auto-resize after a short delay to ensure DOM is updated
    setTimeout(() => {
      const textarea = document.querySelector(`textarea[name="${fieldName}"]`) as HTMLTextAreaElement;
      if (textarea) {
        autoResizeTextarea(textarea);
      }
    }, 0);
  };

  const appendClause = (fieldName: keyof AwardSheetFormValues, text: string) => {
    const current = String(form.values[fieldName] ?? '');
    handleTextareaChange(fieldName, `${current}${text}`);
  };

  useEffect(() => {
    if (resolvedMeetingId) {
      form.setFieldValue('meetingId', resolvedMeetingId);
    }
  }, [resolvedMeetingId]);

  // Load award sheet data in edit mode
  useEffect(() => {
    if (isEditMode && awardSheet) {
      form.setValues({
        meetingId: awardSheet.meetingId,
        tenderId: awardSheet.tenderId || '',
        singleSourceId: awardSheet.singleSourceId || '',
        projectSummarySheetId: awardSheet.projectSummarySheetId || '',
        tenderNumber: awardSheet.tenderNumber,
        tenderName: awardSheet.tenderName,
        location: awardSheet.location,
        quotationNumber: awardSheet.quotationNumber || '',
        vendor: awardSheet.vendor,
        cost: Number(awardSheet.proposedCost),
        duration: awardSheet.proposedDuration,
        decisionText: awardSheet.decisionText,
        justification: awardSheet.justification,
        detail: awardSheet.detail || '',
        acknowledgement: awardSheet.acknowledgement || '',
      });
      setSelectedAgendaItemIds(
        (awardSheet.agendaLinks || []).map((link) => link.agendaItemId)
      );
    }
  }, [isEditMode, awardSheet, form]);

  // Initialize Thaana keyboard for Thaana fields
  useEffect(() => {
    let _keyboardInstance: any = null;
    let observer: MutationObserver | null = null;
    const listeners: Array<{ element: HTMLElement; events: string[]; handler: () => void }> = [];

    import('thaana-keyboard/ThaanaKeyboard.js').then(() => {
      if ((window as any).ThaanaKeyboard) {
        _keyboardInstance = new (window as any).ThaanaKeyboard('.thaana-input');
        
        const syncThaanaField = (inputElement: HTMLInputElement | HTMLTextAreaElement) => {
          const fieldName = inputElement.getAttribute('name');
          if (!fieldName) return;
          
          const currentForm = formRef.current;
          if (!currentForm || !currentForm.values.hasOwnProperty(fieldName)) return;
          
          const currentValue = inputElement.value;
          const formValue = currentForm.values[fieldName as keyof AwardSheetFormValues];
          
          // Always sync DOM value to form state to prevent loss on re-render
          if (currentValue !== formValue) {
            currentForm.setFieldValue(fieldName as keyof AwardSheetFormValues, currentValue);
          }
        };
        
        const findThaanaInputs = (): (HTMLInputElement | HTMLTextAreaElement)[] => {
          const inputs: (HTMLInputElement | HTMLTextAreaElement)[] = [];
          const containers = document.querySelectorAll('.thaana-input');
          
          containers.forEach((container) => {
            const input = container.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement;
            if (input) {
              inputs.push(input);
            }
          });
          
          return inputs;
        };
        
        const setupThaanaListeners = () => {
          // Remove old listeners
          listeners.forEach(({ element, events, handler }) => {
            events.forEach(event => {
              element.removeEventListener(event, handler);
            });
          });
          listeners.length = 0;

          const inputs = findThaanaInputs();
          inputs.forEach((input) => {
            // Sync on multiple events to catch all changes
            const handler = () => syncThaanaField(input);
            const events = ['input', 'change', 'blur', 'focus'];
            
            events.forEach(event => {
              input.addEventListener(event, handler);
            });
            
            listeners.push({ element: input, events, handler });
          });
        };
        
        // Initial setup
        setTimeout(() => {
          setupThaanaListeners();
        }, 100);
        
        // Re-setup listeners when DOM changes
        observer = new MutationObserver(() => {
          setTimeout(() => {
            setupThaanaListeners();
          }, 50);
        });
        
        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });

        // Sync all Thaana fields before any form interaction
        const syncAllThaanaFields = () => {
          const inputs = findThaanaInputs();
          inputs.forEach(input => {
            if (document.activeElement !== input) {
              syncThaanaField(input);
            }
          });
        };

        // Sync before any field interaction
        document.addEventListener('focusin', syncAllThaanaFields);
        document.addEventListener('click', syncAllThaanaFields);
        
        return () => {
          if (observer) {
            observer.disconnect();
          }
          listeners.forEach(({ element, events, handler }) => {
            events.forEach(event => {
              element.removeEventListener(event, handler);
            });
          });
          document.removeEventListener('focusin', syncAllThaanaFields);
          document.removeEventListener('click', syncAllThaanaFields);
        };
      }
    }).catch((err) => {
      console.error('Failed to load Thaana keyboard:', err);
    });

    // Auto-resize textareas
    const resizeTextareas = () => {
      const textareas = document.querySelectorAll('.auto-resize-textarea textarea');
      textareas.forEach((textarea) => {
        const el = textarea as HTMLTextAreaElement;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      });
    };

    setTimeout(resizeTextareas, 100);
  }, []);

  const eligibleAgendaItems = (meeting?.agendaItems || []).filter((item: AgendaItem) => {
    const linkedElsewhere = item.awardSheetLinks?.some(
      (link) => link.awardSheetId !== id
    );
    return !linkedElsewhere;
  });

  const getProcurementKey = (item: AgendaItem) => {
    if (item.tenderId) return `tender:${item.tenderId}`;
    if (item.singleSourceId) return `ss:${item.singleSourceId}`;
    if (item.projectSummarySheetId) return `pss:${item.projectSummarySheetId}`;
    return 'none';
  };

  const getAgendaProcurementRef = (item: AgendaItem) =>
    item.tender?.referenceNo ??
    item.singleSource?.formNumber ??
    item.projectSummarySheet?.summarySheetNumber;

  const selectedProcurementItem = eligibleAgendaItems.find((i) =>
    selectedAgendaItemIds.includes(i.id)
  );
  const selectedProcurementRef = selectedProcurementItem
    ? getAgendaProcurementRef(selectedProcurementItem)
    : '';

  const prefillFromAgendaItem = (item: AgendaItem) => {
    if (item.tender) {
      form.setFieldValue('tenderId', item.tender.id);
      form.setFieldValue('singleSourceId', '');
      form.setFieldValue('projectSummarySheetId', '');
      form.setFieldValue('tenderNumber', item.tender.referenceNo);
      if (!form.values.tenderName) {
        form.setFieldValue('tenderName', item.tender.description);
      }
    } else if (item.singleSource) {
      const ss = item.singleSource as AgendaItem['singleSource'] & {
        projectLocation?: string;
        projectName?: string;
        proposedDuration?: number;
      };
      form.setFieldValue('singleSourceId', ss!.id);
      form.setFieldValue('tenderId', '');
      form.setFieldValue('projectSummarySheetId', '');
      form.setFieldValue('tenderNumber', '');
      form.setFieldValue('tenderName', ss!.projectName ?? ss!.title);
      form.setFieldValue('location', ss!.projectLocation ?? form.values.location);
      form.setFieldValue('vendor', ss!.vendorName);
      if (ss!.proposedCost) {
        form.setFieldValue('cost', Number(ss!.proposedCost));
      }
      if (ss!.proposedDuration) {
        form.setFieldValue('duration', Number(ss!.proposedDuration));
      }
    } else if (item.projectSummarySheet) {
      const pss = item.projectSummarySheet;
      form.setFieldValue('projectSummarySheetId', pss.id);
      form.setFieldValue('tenderId', '');
      form.setFieldValue('singleSourceId', '');
      form.setFieldValue('tenderNumber', '');
      form.setFieldValue('tenderName', pss.projectName);
    }
  };

  const handleAgendaItemSelection = (agendaItemIds: string[]) => {
    setAgendaSelectionError(null);
    const items = eligibleAgendaItems.filter((i) => agendaItemIds.includes(i.id));

    if (items.length > 0) {
      const procurementKeys = new Set(
        items.map(getProcurementKey).filter((key) => key !== 'none')
      );
      if (procurementKeys.size > 1) {
        setAgendaSelectionError(
          'All selected agenda items must refer to the same tender, single-source form, or project summary sheet'
        );
        return;
      }
    }

    setSelectedAgendaItemIds(agendaItemIds);

    if (agendaItemIds.length === 0) {
      form.setFieldValue('tenderId', '');
      form.setFieldValue('singleSourceId', '');
      form.setFieldValue('projectSummarySheetId', '');
      form.setFieldValue('tenderNumber', '');
      return;
    }

    const first = eligibleAgendaItems.find((i) => i.id === agendaItemIds[0]);
    if (first) {
      prefillFromAgendaItem(first);
    }
  };

  const handleSubmit = async (values: AwardSheetFormValues) => {
    if (selectedAgendaItemIds.length === 0) {
      setAgendaSelectionError('Select at least one agenda item');
      return;
    }
    if (agendaSelectionError) {
      return;
    }

    try {
      if (isEditMode && id) {
        await updateAwardSheet.mutateAsync({
          id,
          data: {
            meetingId: values.meetingId,
            agendaItemIds: selectedAgendaItemIds,
            tenderNumber: values.tenderNumber,
            tenderName: values.tenderName,
            location: values.location,
            quotationNumber: values.quotationNumber,
            vendor: values.vendor,
            cost: values.cost,
            duration: values.duration,
            decisionText: values.decisionText,
            justification: values.justification,
            detail: values.detail,
            acknowledgement: values.acknowledgement,
          },
        });
      } else {
        await createAwardSheet.mutateAsync({
          meetingId: values.meetingId,
          agendaItemIds: selectedAgendaItemIds,
          tenderNumber: values.tenderNumber,
          tenderName: values.tenderName,
          location: values.location,
          quotationNumber: values.quotationNumber,
          vendor: values.vendor,
          cost: values.cost,
          duration: values.duration,
          decisionText: values.decisionText,
          justification: values.justification,
          detail: values.detail,
          acknowledgement: values.acknowledgement,
        });
      }
      
      if (isEditMode && id) {
        navigate(`/bid-committee/award-sheets/${id}`);
      } else {
        navigate(`/bid-committee/meetings/${values.meetingId}`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} award sheet:`, error);
    }
  };

  if (meetingLoading || (isEditMode && awardSheetLoading)) {
    return (
      <Container size="lg" py="xl">
        <Stack align="center">
          <Loader size="lg" />
          <Text>Loading...</Text>
        </Stack>
      </Container>
    );
  }

  if (!meeting && !isEditMode) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Meeting not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Title order={2}>{isEditMode ? 'Edit Award Sheet' : 'Create Award Sheet'}</Title>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <Card withBorder p="md">
              <Stack gap="md">
                <Title order={4}>Meeting Information</Title>
                <TextInput
                  label="Meeting"
                  value={meeting?.title || ''}
                  disabled
                />
              </Stack>
            </Card>

            <Card withBorder p="md">
              <Stack gap="md">
                <Title order={4}>Agenda items &amp; procurement</Title>

                <MultiSelect
                  label="Agenda items"
                  placeholder="Select agenda items for this award sheet"
                  data={eligibleAgendaItems.map((item) => {
                    const ref = item.tender
                      ? item.tender.referenceNo
                      : item.singleSource?.formNumber
                        ?? item.projectSummarySheet?.summarySheetNumber;
                    return {
                      value: item.id,
                      label: `${item.orderNo}. ${item.subject}${ref ? ` (${ref})` : ''}`,
                    };
                  })}
                  value={selectedAgendaItemIds}
                  onChange={handleAgendaItemSelection}
                  required
                  searchable
                />

                {agendaSelectionError && (
                  <Alert icon={<IconAlertCircle size={16} />} color="red">
                    {agendaSelectionError}
                  </Alert>
                )}

                {eligibleAgendaItems.length === 0 && (
                  <Alert icon={<IconAlertCircle size={16} />} color="yellow">
                    No eligible agenda items. Add agenda items to this meeting that are not already
                    linked to another award sheet.
                  </Alert>
                )}

                {(form.values.tenderId ||
                  form.values.singleSourceId ||
                  form.values.projectSummarySheetId) && (
                  <Text size="sm" c="dimmed">
                    Linked procurement:{' '}
                    {form.values.tenderId
                      ? `Tender${selectedProcurementRef ? ` ${selectedProcurementRef}` : ''}`
                      : form.values.singleSourceId
                        ? `Single source${selectedProcurementRef ? ` ${selectedProcurementRef}` : ''}`
                        : `Project summary PR-03${selectedProcurementRef ? ` ${selectedProcurementRef}` : ''}`}
                  </Text>
                )}

                <TextInput
                  label="Tender Number (optional)"
                  placeholder="Leave blank if not applicable"
                  {...form.getInputProps('tenderNumber')}
                  readOnly={!!form.values.tenderId}
                />
              </Stack>
            </Card>

            <Card withBorder p="md">
              <Stack gap="md">
                <Title order={4}>Award Details</Title>
                
                <div className="thaana-input" style={{ direction: 'rtl', textAlign: 'right' }}>
                  <TextInput
                    label="Tender Name (Thaana, optional)"
                    name="tenderName"
                    value={form.values.tenderName}
                    onChange={(e) => {
                      syncThaanaFieldsBeforeRender();
                      form.setFieldValue('tenderName', e.currentTarget.value);
                    }}
                    onBlur={() => syncThaanaFieldsBeforeRender()}
                    styles={{
                      input: {
                        direction: 'rtl',
                        textAlign: 'right',
                        fontFamily: 'Faruma, Arial, sans-serif'
                      }
                    }}
                  />
                </div>

                <div className="thaana-input" style={{ direction: 'rtl', textAlign: 'right' }}>
                  <TextInput
                    label="Location (Thaana)"
                    name="location"
                    value={form.values.location}
                    onChange={(e) => {
                      syncThaanaFieldsBeforeRender();
                      form.setFieldValue('location', e.currentTarget.value);
                    }}
                    onBlur={() => syncThaanaFieldsBeforeRender()}
                    required
                    styles={{
                      input: {
                        direction: 'rtl',
                        textAlign: 'right',
                        fontFamily: 'Faruma, Arial, sans-serif'
                      }
                    }}
                  />
                </div>

                <TextInput
                  label="Quotation Number"
                  {...form.getInputProps('quotationNumber')}
                />

                <div className="thaana-input" style={{ direction: 'rtl', textAlign: 'right' }}>
                  <TextInput
                    label="Vendor (Thaana)"
                    name="vendor"
                    value={form.values.vendor}
                    onChange={(e) => {
                      syncThaanaFieldsBeforeRender();
                      form.setFieldValue('vendor', e.currentTarget.value);
                    }}
                    onBlur={() => syncThaanaFieldsBeforeRender()}
                    required
                    styles={{
                      input: {
                        direction: 'rtl',
                        textAlign: 'right',
                        fontFamily: 'Faruma, Arial, sans-serif'
                      }
                    }}
                  />
                </div>

                <NumberInput
                  label="Proposed Cost (MVR)"
                  {...form.getInputProps('cost')}
                  required
                  min={0}
                  decimalScale={2}
                  thousandSeparator=","
                />

                <NumberInput
                  label="Proposed Duration (days)"
                  {...form.getInputProps('duration')}
                  required
                  min={1}
                />
              </Stack>
            </Card>

            <Card withBorder p="md">
              <Stack gap="md">
                <Title order={4}>Decision and Justification</Title>

                <Group justify="flex-end">
                  <RegulatoryClausePicker onInsert={(text) => appendClause('decisionText', text)} />
                </Group>
                <div className="thaana-input auto-resize-textarea" style={{ direction: 'rtl', textAlign: 'right' }}>
                  <Textarea
                    label="Decision Text (Thaana)"
                    name="decisionText"
                    value={form.values.decisionText}
                    onChange={(e) => {
                      handleTextareaChange('decisionText', e.currentTarget.value);
                    }}
                    onKeyDown={(e) => {
                      // Auto-resize on Enter key
                      if (e.key === 'Enter') {
                        setTimeout(() => {
                          const textarea = e.currentTarget as HTMLTextAreaElement;
                          if (textarea) {
                            autoResizeTextarea(textarea);
                          }
                        }, 0);
                      }
                    }}
                    onBlur={() => {
                      syncThaanaFieldsBeforeRender();
                      const textarea = document.querySelector('textarea[name="decisionText"]') as HTMLTextAreaElement;
                      if (textarea) autoResizeTextarea(textarea);
                    }}
                    required
                    minRows={3}
                    autosize
                    styles={{
                      input: {
                        direction: 'rtl',
                        textAlign: 'right',
                        fontFamily: 'Faruma, Arial, sans-serif',
                        resize: 'none',
                        overflow: 'hidden'
                      }
                    }}
                  />
                </div>

                <Group justify="flex-end">
                  <RegulatoryClausePicker onInsert={(text) => appendClause('justification', text)} />
                </Group>
                <div className="thaana-input auto-resize-textarea" style={{ direction: 'rtl', textAlign: 'right' }}>
                  <Textarea
                    label="Justification (Thaana)"
                    name="justification"
                    value={form.values.justification}
                    onChange={(e) => {
                      handleTextareaChange('justification', e.currentTarget.value);
                    }}
                    onKeyDown={(e) => {
                      // Auto-resize on Enter key
                      if (e.key === 'Enter') {
                        setTimeout(() => {
                          const textarea = e.currentTarget as HTMLTextAreaElement;
                          if (textarea) {
                            autoResizeTextarea(textarea);
                          }
                        }, 0);
                      }
                    }}
                    onBlur={() => {
                      syncThaanaFieldsBeforeRender();
                      const textarea = document.querySelector('textarea[name="justification"]') as HTMLTextAreaElement;
                      if (textarea) autoResizeTextarea(textarea);
                    }}
                    required
                    minRows={3}
                    autosize
                    styles={{
                      input: {
                        direction: 'rtl',
                        textAlign: 'right',
                        fontFamily: 'Faruma, Arial, sans-serif',
                        resize: 'none',
                        overflow: 'hidden'
                      }
                    }}
                  />
                </div>

                <Group justify="flex-end">
                  <RegulatoryClausePicker onInsert={(text) => appendClause('detail', text)} />
                </Group>
                <div className="thaana-input auto-resize-textarea" style={{ direction: 'rtl', textAlign: 'right' }}>
                  <Textarea
                    label="Detail (Thaana)"
                    name="detail"
                    value={form.values.detail || ''}
                    onChange={(e) => {
                      handleTextareaChange('detail', e.currentTarget.value);
                    }}
                    onKeyDown={(e) => {
                      // Auto-resize on Enter key
                      if (e.key === 'Enter') {
                        setTimeout(() => {
                          const textarea = e.currentTarget as HTMLTextAreaElement;
                          if (textarea) {
                            autoResizeTextarea(textarea);
                          }
                        }, 0);
                      }
                    }}
                    onBlur={() => {
                      syncThaanaFieldsBeforeRender();
                      const textarea = document.querySelector('textarea[name="detail"]') as HTMLTextAreaElement;
                      if (textarea) autoResizeTextarea(textarea);
                    }}
                    minRows={3}
                    autosize
                    styles={{
                      input: {
                        direction: 'rtl',
                        textAlign: 'right',
                        fontFamily: 'Faruma, Arial, sans-serif',
                        resize: 'none',
                        overflow: 'hidden'
                      }
                    }}
                  />
                </div>

                <Group justify="flex-end">
                  <RegulatoryClausePicker onInsert={(text) => appendClause('acknowledgement', text)} />
                </Group>
                <div className="thaana-input auto-resize-textarea" style={{ direction: 'rtl', textAlign: 'right' }}>
                  <Textarea
                    label="Acknowledgement (Thaana)"
                    name="acknowledgement"
                    value={form.values.acknowledgement || ''}
                    onChange={(e) => {
                      handleTextareaChange('acknowledgement', e.currentTarget.value);
                    }}
                    onKeyDown={(e) => {
                      // Auto-resize on Enter key
                      if (e.key === 'Enter') {
                        setTimeout(() => {
                          const textarea = e.currentTarget as HTMLTextAreaElement;
                          if (textarea) {
                            autoResizeTextarea(textarea);
                          }
                        }, 0);
                      }
                    }}
                    onBlur={() => {
                      syncThaanaFieldsBeforeRender();
                      const textarea = document.querySelector('textarea[name="acknowledgement"]') as HTMLTextAreaElement;
                      if (textarea) autoResizeTextarea(textarea);
                    }}
                    minRows={3}
                    autosize
                    styles={{
                      input: {
                        direction: 'rtl',
                        textAlign: 'right',
                        fontFamily: 'Faruma, Arial, sans-serif',
                        resize: 'none',
                        overflow: 'hidden'
                      }
                    }}
                  />
                </div>
              </Stack>
            </Card>

            <Group justify="flex-end" mt="md">
              <Button
                variant="outline"
                onClick={() => navigate(`/bid-committee/meetings/${form.values.meetingId}`)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isEditMode ? updateAwardSheet.isPending : createAwardSheet.isPending}
              >
                {isEditMode ? 'Update Award Sheet' : 'Create Award Sheet'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Container>
  );
}

